import { MixerFaderState } from "../display/Mixer.js";

export interface MidiDeviceOptions {

}

export type MidiDeviceState = "connected" | "disconnected" | "ready" | "error";

export interface MidiDeviceFader extends MixerFaderState {
    ready: boolean;
    raw: number;
}

export class MidiDevice {

    public readonly onChange = e5.Signal.create<[MidiDeviceFader[]]>();
    public readonly onChangeFader = e5.Signal.create<[MidiDeviceFader]>();
    public readonly onStateChange = e5.Signal.create<[MidiDeviceState]>();

    private _access: WebMidi.MIDIAccess;
    private _faders: MidiDeviceFader[] = [];
    private _fadersLookup: { [key: number]: MidiDeviceFader } = {};
    private _options: MidiDeviceOptions;
    private _connected: boolean = false;
    private _state: MidiDeviceState = "disconnected";
    private _map: { [key: number]: number } =
        {
            32: 0,
            33: 1,
            34: 2,
            35: 3,
            36: 4,
            37: 5,
            38: 6,
            39: 7,
            40: 8,
            41: 9,
            42: 10,
            43: 11,
            44: 12,
            45: 13,
            46: 14,
            47: 15
        };

    public static get isSupported(): boolean {
        return Boolean(navigator.requestMIDIAccess);
    }

    constructor(options: MidiDeviceOptions) {

        options = e5.object.merge({
        } as MidiDeviceOptions, options);
        this._options = options;

        this.handleMidiMessage = this.handleMidiMessage.bind(this);
        this.handleStateChange = this.handleStateChange.bind(this);

        if (!MidiDevice.isSupported) {
            throw new Error("Web MIDI API is not supported!");
        }

        for (var key in this._map) {
            var index = this._map[key];
            var fader: MidiDeviceFader = {
                index: index,
                ready: false,
                raw: 0,
                volume: 0.5
            };
            this._fadersLookup[key] = fader;
            this._faders[index] = fader;
        }

        this.onChangeFader.throttle(() => {
            this.onChange.dispatch(this._faders);
        }, 50);

        this.connect();
    }

    public forceReady():void{
        this.setState("ready");
    }

    public onReady(): Promise<MidiDevice> {
        if (this._state === "ready") {
            return Promise.resolve(this);
        }
        return new Promise((resolve) => {
            var disposable = this.onStateChange.add((state) => {
                if (state === "ready") {
                    disposable.dispose();
                    resolve(this);
                }
            });
        });
    }

    private async connect() {

        try {
            this._access = await navigator.requestMIDIAccess({
                sysex: true
            });

            this._access.onstatechange = this.handleStateChange;

            await this.updateInputs();
            await this.updateOutputs();

            this.updateState();

        } catch (error) {
            console.error("Request MIDI access failed!", error);
            this.setState("error");
        }
    }

    private updateState(): void {
        if (this._access.inputs.size === 0) {
            this.setState("disconnected");
            return;
        }

        for (var input of this._access.inputs.values()) {
            if (input.connection !== "open") {
                this.setState("disconnected");
                return;
            }
        }

        for (var output of this._access.outputs.values()) {
            if (output.connection !== "open") {
                this.setState("disconnected");
                return;
            }
        }

        var ready = true;
        for (var fader of this._faders) {
            if (!fader.ready) {
                ready = false;
                break;
            }
        }

        if (ready) {
            this.setState("ready");
        } else {
            this.setState("connected");
        }
    }

    public getFader(index: number): MidiDeviceFader {
        return this._faders[index];
    }

    public getVolume(index: number, fallback: number = 0.5): number {
        var volume = this.getFader(index)?.volume;
        return volume === null ? fallback : volume;
    }

    public get faders(): MidiDeviceFader[] {
        return this._faders;
    }

    private async updateInputs() {

        if (this._access.inputs.size === 0) {
            // console.warn("Please connect a MIDI device!");
        }

        for (var input of this._access.inputs.values()) {
            if (input.connection === "closed") {
                this._access.onstatechange = null;
                await input.open();
                this._access.onstatechange = this.handleStateChange;
            }
            input.onmidimessage = this.handleMidiMessage;
        }
    }

    private async updateOutputs() {
        if (this._access.outputs.size === 0) {
            // console.warn("Please connect a MIDI device!");
        }

        for (var output of this._access.outputs.values()) {
            if (output.connection === "closed") {
                this._access.onstatechange = null;
                await output.open();
                this._access.onstatechange = this.handleStateChange;
            }

            var sysex = 0xF0;
            var sysexend = 0xF7;
            var sysexMfgId = [0x7d, 0x00, 0x00];
            var requestInfoMsg = 0x1f;
            output.send([sysex, ...sysexMfgId, requestInfoMsg, sysexend]);
        }
    }

    private handleStateChange(e: WebMidi.MIDIConnectionEvent): void {
        if (e.port.type === "input") {
            this.updateInputs();
        }
        else if (e.port.type === "output") {
            this.updateOutputs();
        }
        this.updateState();
    }

    private setState(state: MidiDeviceState) {
        if (this._state === state) {
            return;
        }
        this._state = state;

        if (state === "disconnected") {
            for (var fader of this._faders) {
                fader.ready = false;
            }
        }

        this.onStateChange.dispatch(state);
    }

    public get state(): MidiDeviceState {
        return this._state;
    }

    private handleMidiMessage(e: WebMidi.MIDIMessageEvent): void {
        //Sysex response
        if (e.data[0] == 0xf0
            && e.data[1] == 0x7d
            && e.data[2] == 0x00
            && e.data[3] == 0x00) {
            //@see https://github.com/16n-faderbank/16n-editor/blob/main/src/Configuration.js
            // console.log(e.data.slice(4, e.data.length - 1));
            return;
        }

        var fader = this._fadersLookup[e.data[1]];
        if (!fader) {
            return;
        }

        var changeReadyState = !fader.ready;
        fader.ready = true;
        fader.raw = e.data[2];
        fader.volume = e.data[2] / 127;

        /**
         * Fixes that the values are not linear.
         * The values are base 2 logarithmic! So if you set the 
         * fader in the middle its not 50%.
         * 
         * @see https://github.com/16n-faderbank/16n/issues/47
         */
        fader.volume = Math.log2(1 + fader.volume);
        // fader.volume = Math.log1p(fader.volume) * (1 / Math.LN2);

        if (changeReadyState) {
            this.updateState();
        }
        this.onChangeFader.dispatch(fader);
    }
}